/* eslint-disable */
import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  InputBase,
  Button,
  IconButton,
  Avatar,
  useMediaQuery,
  useTheme,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  Favorite as HeartIcon,
  Person as UserIcon,
  Logout as LogOutIcon,
  Home as HomeIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { useToast } from "../context/ToastContext";
import { useLocation, useNavigate } from "react-router-dom";
import allPropertiesIcon from "../Assets/images/svg/all-properties.svg";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.spacing(3),
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.15)
      : alpha(theme.palette.common.white, 0.95),
  border: `2px solid ${
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.2)
      : alpha(theme.palette.primary.main, 0.1)
  }`,
  boxShadow:
    theme.palette.mode === "dark" ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
  transition: theme.transitions.create([
    "background-color",
    "border-color",
    "box-shadow",
  ]),
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.25)
        : alpha(theme.palette.common.white, 1),
    borderColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.3)
        : alpha(theme.palette.primary.main, 0.3),
    boxShadow:
      theme.palette.mode === "dark" ? "none" : "0 4px 12px rgba(0,0,0,0.12)",
  },
  "&:focus-within": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.25)
        : alpha(theme.palette.common.white, 1),
    borderColor: theme.palette.primary.main,
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`
        : `0 0 0 3px ${alpha(
            theme.palette.primary.main,
            0.1
          )}, 0 4px 12px rgba(0,0,0,0.12)`,
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.7)
      : theme.palette.primary.main,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color:
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.text.primary,
  width: "100%",
  fontWeight: 500,
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.25, 1, 1.25, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    paddingRight: theme.spacing(10),
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: {
      width: "28ch",
      "&:focus": {
        width: "35ch",
      },
    },
    [theme.breakpoints.up("md")]: {
      width: "32ch",
      "&:focus": {
        width: "40ch",
      },
    },
    "&::placeholder": {
      color:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.7)
          : theme.palette.text.secondary,
      opacity: 1,
      fontWeight: 400,
    },
  },
}));

export function Header({
  onListPropertyClick,
  isLoggedIn = false,
  isAdmin = false,
  userEmail,
  onSignIn,
  onSignOut,
  onSearch,
  onMyPropertiesClick,
  onManageMyPropertiesClick,
  onAdminPortalClick,
  onUnderVerificationClick,
  onUsersClick,
  onHomeClick,
  onSavedPropertiesClick,
  onMyProfileClick,
  hideSearchOnMobile = false,
}) {
  const theme = useTheme();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      return sessionStorage.getItem("propertySearchQuery") || "";
    } catch (error) {
      return "";
    }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const openProfile = Boolean(anchorElProfile);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(true);

  const placeholderTexts = [
    "Search by location, property type...",
    'Try "Udupi villa"...',
    'Search "Mangalore apartment"...',
    'Find "Commercial space"...',
    'Look for "Agricultural land"...',
    'Explore "Beach front property"...',
  ];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e && e.key === "Enter" && searchQuery.trim()) {
      if (onSearch) {
        onSearch(searchQuery.trim());
      }
    }
  };

  const handleSearchButtonClick = () => {
    if (searchQuery.trim() && onSearch) {
      onSearch(searchQuery.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    // Immediately trigger search with empty string to show all properties
    if (onSearch) {
      onSearch("");
      toast.showInfo("Showing all properties");
    }
  };

  // Animated placeholder effect
  useEffect(() => {
    const currentText = placeholderTexts[placeholderIndex];
    let timeout;

    if (isTyping) {
      // Typing effect
      if (displayedPlaceholder.length < currentText.length) {
        timeout = setTimeout(() => {
          setDisplayedPlaceholder(
            currentText.slice(0, displayedPlaceholder.length + 1)
          );
        }, 80); // Typing speed
      } else {
        // Finished typing, wait before deleting
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, 2000); // Pause duration
      }
    } else {
      // Deleting effect
      if (displayedPlaceholder.length > 0) {
        timeout = setTimeout(() => {
          setDisplayedPlaceholder(displayedPlaceholder.slice(0, -1));
        }, 50); // Deleting speed
      } else {
        // Finished deleting, move to next text
        setPlaceholderIndex(
          (prevIndex) => (prevIndex + 1) % placeholderTexts.length
        );
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayedPlaceholder, isTyping, placeholderIndex]);

  // Cursor blinking effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500); // Blink every 500ms

    return () => clearInterval(cursorInterval);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const queryFromUrl = (params.get("q") || "").trim();
    const queryFromState = String(location?.state?.seoSearchQuery || "").trim();

    if (queryFromState) {
      setSearchQuery(queryFromState);
      return;
    }

    if (queryFromUrl) {
      setSearchQuery(queryFromUrl);
      return;
    }

    if (location.pathname === "/") {
      try {
        const storedQuery = sessionStorage.getItem("propertySearchQuery") || "";
        setSearchQuery(storedQuery);
      } catch (error) {
        setSearchQuery("");
      }
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const shouldClearSearch =
      location.pathname.startsWith("/users") ||
      location.pathname.startsWith("/admin-portal") ||
      location.pathname.startsWith("/my-profile");

    if (shouldClearSearch) {
      setSearchQuery("");
      try {
        sessionStorage.removeItem("propertySearchQuery");
        sessionStorage.setItem("propertyIsSearchMode", "false");
      } catch (error) {
        // Ignore storage access errors.
      }
    }
  }, [location.pathname]);

  const handleLogoClick = () => {
    if (onHomeClick) {
      onHomeClick();
    }
    closeMobileMenu();
  };

  const handleProfileClick = (event) => {
    setAnchorElProfile(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorElProfile(null);
  };

  const handleAdminPortalMenuClick = () => {
    handleProfileClose();
    if (onAdminPortalClick) {
      onAdminPortalClick();
      return;
    }
    navigate("/admin-portal");
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        zIndex: theme.zIndex.appBar,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          }}
          onClick={handleLogoClick}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              display: "block",
            }}
          >
            Vruddhi Properties
          </Typography>
        </Box>

        {/* Search Bar - Hidden on mobile */}
        {!isMobile && (
          <Search sx={{ flexGrow: 1, maxWidth: 400, mx: 4 }}>
            <StyledInputBase
              placeholder={`${displayedPlaceholder}${showCursor ? "|" : ""}`}
              inputProps={{ "aria-label": "search" }}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchSubmit}
            />
            <Box
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: 0.5,
              }}
            >
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      bgcolor: "action.hover",
                      color: "text.primary",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={handleSearchButtonClick}
                disabled={!searchQuery.trim()}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                  "&.Mui-disabled": {
                    color: "text.disabled",
                  },
                }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>
          </Search>
        )}

        {/* User Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Authentication actions - Desktop */}
          {isLoggedIn ? (
            <>
              {/* Profile Icon Button - Desktop only */}
              <Avatar
                onClick={handleProfileClick}
                aria-label="account menu"
                aria-controls={openProfile ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={openProfile ? "true" : undefined}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "primary.main",
                  cursor: "pointer",
                  display: { xs: "none", lg: "flex" },
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "primary.dark",
                    transform: "scale(1.05)",
                    boxShadow: 2,
                  },
                  fontWeight: 600,
                  fontSize: "1rem",
                }}
              >
                {userEmail?.charAt(0).toUpperCase() || "U"}
              </Avatar>

              {/* Profile Dropdown Menu */}
              <Menu
                id="account-menu"
                anchorEl={anchorElProfile}
                open={openProfile}
                onClose={handleProfileClose}
                MenuListProps={{
                  "aria-labelledby": "account-button",
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                sx={{
                  mt: 1,
                  "& .MuiPaper-root": {
                    minWidth: 220,
                  },
                }}
              >
                {/* User Email Header */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    Signed in as
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    fontWeight={600}
                    sx={{
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                    }}
                  >
                    {userEmail}
                  </Typography>
                </Box>

                {/* My Profile */}
                <MenuItem
                  onClick={() => {
                    handleProfileClose();
                    if (onMyProfileClick) {
                      onMyProfileClick();
                    }
                  }}
                >
                  <ListItemIcon>
                    <UserIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>My Profile</ListItemText>
                </MenuItem>

                {/* Saved Properties */}
                <MenuItem
                  onClick={() => {
                    handleProfileClose();
                    if (onSavedPropertiesClick) {
                      onSavedPropertiesClick();
                    }
                  }}
                >
                  <ListItemIcon>
                    <HeartIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Saved Properties</ListItemText>
                </MenuItem>

                {/* My Listing */}
                <MenuItem
                  onClick={() => {
                    handleProfileClose();
                    if (onMyPropertiesClick) {
                      onMyPropertiesClick();
                    }
                  }}
                >
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>My Listing</ListItemText>
                </MenuItem>

                {/* My Properties */}
                <MenuItem
                  onClick={() => {
                    handleProfileClose();
                    if (onManageMyPropertiesClick) {
                      onManageMyPropertiesClick();
                    }
                  }}
                >
                  <ListItemIcon>
                    <DescriptionIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>My Properties</ListItemText>
                </MenuItem>

                {isAdmin && (
                  <MenuItem
                    onClick={handleAdminPortalMenuClick}
                  >
                    <ListItemIcon>
                      <Box
                        component="img"
                        src={allPropertiesIcon}
                        alt="Admin Portal"
                        sx={{ width: 18, height: 18 }}
                      />
                    </ListItemIcon>
                    <ListItemText>Admin Portal</ListItemText>
                  </MenuItem>
                )}

                <Divider />

                {/* Sign Out */}
                <MenuItem
                  onClick={() => {
                    handleProfileClose();
                    if (onSignOut) {
                      onSignOut();
                    }
                  }}
                >
                  <ListItemIcon>
                    <LogOutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Sign Out</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              color="inherit"
              startIcon={<UserIcon />}
              onClick={onSignIn}
              sx={{
                textTransform: "none",
                display: { xs: "none", lg: "flex" },
              }}
            >
              Sign In
            </Button>
          )}

          {/*
          <Button
            variant="contained"
            onClick={onListPropertyClick}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              ml: 1,
            }}
          >
            List Property
          </Button>
          */}

          <IconButton
            color="inherit"
            sx={{ display: { lg: "none" } }}
            onClick={toggleMobileMenu}
            aria-label="open mobile menu"
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>

      {/* Mobile Search */}
      {isMobile && !hideSearchOnMobile && (
        <Box sx={{ p: 2, pt: 0 }}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder={`${displayedPlaceholder}${showCursor ? "|" : ""}`}
              inputProps={{ "aria-label": "search" }}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchSubmit}
            />
            <Box
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: 0.5,
              }}
            >
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      bgcolor: "action.hover",
                      color: "text.primary",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={handleSearchButtonClick}
                disabled={!searchQuery.trim()}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                  "&.Mui-disabled": {
                    color: "text.disabled",
                  },
                }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>
          </Search>
        </Box>
      )}

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
        }}
      >
        <Box sx={{ width: 280 }} role="presentation">
          {/* Drawer Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Menu
            </Typography>
            <IconButton onClick={closeMobileMenu} aria-label="close menu">
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            {/* Home */}
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogoClick}>
                <ListItemIcon>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText primary="Home" />
              </ListItemButton>
            </ListItem>

            {/* Logged-in user menu items */}
            {isLoggedIn && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (onMyProfileClick) {
                        onMyProfileClick();
                      }
                      closeMobileMenu();
                    }}
                  >
                    <ListItemIcon>
                      <UserIcon />
                    </ListItemIcon>
                    <ListItemText primary="My Profile" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (onMyPropertiesClick) {
                        onMyPropertiesClick();
                      }
                      closeMobileMenu();
                    }}
                  >
                    <ListItemIcon>
                      <BusinessIcon />
                    </ListItemIcon>
                    <ListItemText primary="My Listing" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (onManageMyPropertiesClick) {
                        onManageMyPropertiesClick();
                      }
                      closeMobileMenu();
                    }}
                  >
                    <ListItemIcon>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText primary="My Properties" />
                  </ListItemButton>
                </ListItem>

                {isAdmin && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        if (onAdminPortalClick) {
                          onAdminPortalClick();
                        } else {
                          navigate('/admin-portal');
                        }
                        closeMobileMenu();
                      }}
                    >
                      <ListItemIcon>
                        <DescriptionIcon />
                      </ListItemIcon>
                      <ListItemText primary="Admin Portal" />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (onSavedPropertiesClick) {
                        onSavedPropertiesClick();
                      }
                      closeMobileMenu();
                    }}
                  >
                    <ListItemIcon>
                      <HeartIcon />
                    </ListItemIcon>
                    <ListItemText primary="Saved Properties" />
                  </ListItemButton>
                </ListItem>
              </>
            )}

            <Divider sx={{ my: 1 }} />

            {/* User Profile Info (if logged in) */}
            {isLoggedIn && userEmail && (
              <ListItem>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="caption" color="text.secondary">
                    Logged in as:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      wordBreak: "break-word",
                    }}
                  >
                    {userEmail}
                  </Typography>
                </Box>
              </ListItem>
            )}

            {/* Authentication Actions */}
            {isLoggedIn ? (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (onSignOut) {
                      onSignOut();
                    }
                    closeMobileMenu();
                  }}
                >
                  <ListItemIcon>
                    <LogOutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign Out" />
                </ListItemButton>
              </ListItem>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (onSignIn) {
                      onSignIn();
                    }
                    closeMobileMenu();
                  }}
                >
                  <ListItemIcon>
                    <UserIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign In" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
